'use client';

import { useEditQuantity } from '@veranda/shared/cart/hooks';
import type { ContextCartItem } from '@veranda/shared/cart/types';
import { cn } from '@veranda/shared/lib';
import { useMaxQuantity } from '@veranda/shared/lib/hooks';

export function EditQuantityButton({
  baseUrl,
  item,
  type,
  className,
}: {
  baseUrl: string;
  item: ContextCartItem;
  type: 'plus' | 'minus';
  className?: string;
}) {
  const { editQuantity, loading } = useEditQuantity({
    domain: baseUrl,
  });
  const maxQuantity = useMaxQuantity({
    productGid: item.productId,
    variantGid: item.variantId,
    allowBackorder: item.allowBackorder,
  });

  const isAtMaxQuantity = type === 'plus' && item.quantity >= maxQuantity;

  const handleClick = () => {
    editQuantity(item, type, maxQuantity);
  };

  const actionText = type === 'plus' ? 'Increase' : 'Decrease';
  const ariaLabel = `${actionText} quantity of ${item.title} by 1`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading || isAtMaxQuantity}
      aria-disabled={loading || isAtMaxQuantity}
      aria-label={ariaLabel}
      className={cn(
        'flex items-center justify-center px-1 py-0.5 hf:text-gray-900 text-[hsl(var(--color))] disabled:opacity-50',
        'min-h-[32px] min-w-[32px]', // Ensure minimum touch target size
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--color))] focus-visible:outline-offset-2',
        className
      )}
    >
      <span className="sr-only">{actionText} quantity by 1</span>
      {type === 'minus' ? (
        <svg
          viewBox="0 0 12 3"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-2.5 w-2.5"
          aria-hidden="true"
        >
          <path
            d="M1.3335 1.5H10.6668"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          viewBox="0 0 12 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-2.5 w-2.5"
          aria-hidden="true"
        >
          <path
            d="M6.00019 1.83331V11.1666M1.3335 6.49998H10.6668"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}
